
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { RecruitmentStatus } from "@/types/test-response";

export function useRecruitmentStatus() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const updateRecruitmentStatus = useMutation({
    mutationFn: async ({ responseId, status, notes }: { responseId: string; status: RecruitmentStatus; notes?: string }) => {
      console.log(`Updating response ${responseId} to status: ${status}`);
      
      const updateData: any = { recruitment_status: status };
      if (notes !== undefined) {
        updateData.recruitment_notes = notes;
      }
      
      const { data, error } = await supabase
        .from('test_responses')
        .update(updateData)
        .eq('id', responseId)
        .select('recruitment_status, recruitment_notes');
      
      if (error) {
        console.error("Error updating recruitment status:", error);
        throw error;
      }
      
      console.log("Update response:", data);
      
      if (!data || data.length === 0) {
        throw new Error('No data returned from update operation');
      }
      
      return data[0];
    },
    onSuccess: (_, variables) => {
      toast({
        title: "Status updated",
        description: `Candidate marked as ${variables.status}.`,
      });
      
      // Invalidate related queries to update UI
      queryClient.invalidateQueries({ queryKey: ['test-responses'] });
      queryClient.invalidateQueries({ queryKey: ['test-response', variables.responseId] });
    },
    onError: (error: any) => {
      console.error("Failed to update recruitment status:", error);
      toast({
        title: "Error updating status",
        description: error.message || "An unknown error occurred",
        variant: "destructive",
      });
    },
  });

  const updateRecruitmentNotes = useMutation({
    mutationFn: async ({ responseId, notes }: { responseId: string; notes: string }) => {
      const { data, error } = await supabase
        .from('test_responses')
        .update({ recruitment_notes: notes })
        .eq('id', responseId)
        .select('recruitment_notes');
      
      if (error) {
        console.error("Error updating recruitment notes:", error);
        throw error;
      }
      
      console.log("Update notes response:", data);
      
      if (!data || data.length === 0) {
        throw new Error('No data returned from update operation');
      }
      
      return data[0];
    },
    onSuccess: (_, variables) => {
      toast({
        title: "Notes saved",
        description: "Recruitment notes have been updated.",
      });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['test-responses'] });
      queryClient.invalidateQueries({ queryKey: ['test-response', variables.responseId] });
    },
    onError: (error: any) => {
      console.error("Failed to update recruitment notes:", error);
      toast({
        title: "Error saving notes",
        description: error.message || "An unknown error occurred",
        variant: "destructive",
      });
    },
  });

  return {
    updateRecruitmentStatus,
    updateRecruitmentNotes,
    isUpdating: updateRecruitmentStatus.isPending || updateRecruitmentNotes.isPending
  };
}
